import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Product, Collection } from '../types';
import ProductCard from '../components/ProductCard';
import { useScrollReveal } from '../hooks/useScrollReveal';

const CollectionProducts = () => {
  const { collectionId } = useParams<{ collectionId: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [collection, setCollection] = useState<Collection | null>(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useScrollReveal([loading]);

  useEffect(() => {
    if (!collectionId) return;

    const fetchData = async () => {
      try {
        const [collRes, prodRes] = await Promise.all([
          fetch(`/api/collections/${collectionId}`),
          fetch(`/api/products/collection/${collectionId}?limit=50`),
        ]);

        const collData = await collRes.json();
        const prodData = await prodRes.json();

        if (collData.success) setCollection(collData.data);
        if (prodData.success) setProducts(prodData.data.products || []);
      } catch {
        // fail silently
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [collectionId]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="spinner-lg" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-offwhite">
      {/* Collection Hero Banner */}
      {collection && (
        <section className="relative h-[40vh] min-h-[280px] overflow-hidden bg-warmgray-200">
          {collection.image && (
            <img
              src={collection.image.startsWith('http') ? collection.image : collection.image}
              alt={collection.name}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-offblack/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-offblack/60 to-transparent" />

          <div className="absolute bottom-10 left-0 right-0 z-10">
            <div className="section-wide">
              <Link
                to="/collections"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-offwhite/70 hover:text-offwhite font-sans mb-4 transition-colors"
              >
                <ArrowLeftIcon className="h-3.5 w-3.5" />
                All Collections
              </Link>
              <h1 className="font-display text-display-lg text-offwhite">{collection.name}</h1>
              {collection.description && (
                <p className="font-sans text-sm text-offwhite/70 mt-2 max-w-lg">
                  {collection.description}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Products */}
      <section className="section-wide py-section">
        <div className="flex items-center justify-between mb-8 reveal">
          <p className="font-sans text-sm text-warmgray-500">
            {products.length} product{products.length !== 1 ? 's' : ''}
          </p>
        </div>

        {products.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {products.map((product, index) => (
              <ProductCard key={product._id} product={product} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 reveal">
            <p className="font-sans text-warmgray-400 mb-6">
              No products in this collection yet.
            </p>
            <Link to="/collections" className="btn-secondary btn-sm">
              Browse Other Collections
            </Link>
          </div>
        )}
      </section>
    </div>
  );
};

export default CollectionProducts;